import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Layout } from "../components/Layout";
import { VirtualBusinessGrid } from "../components/VirtualBusinessGrid";
import { getIcon } from "../lib/icons";
import { fetchCategory, fetchBusinessesPage } from "../lib/api";

const PAGE = 30;

export default function CategoryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const busyRef = useRef(false);

  const load = useCallback(
    async (reset, currentLen = 0) => {
      if (busyRef.current) return;
      busyRef.current = true;
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const offset = reset ? 0 : currentLen;
        const data = await fetchBusinessesPage({ category: slug, limit: PAGE, offset });
        setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        setTotal(data.total);
        setHasMore(data.has_more);
      } catch {
        if (reset) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        busyRef.current = false;
      }
    },
    [slug]
  );

  useEffect(() => {
    fetchCategory(slug).then(setCategory).catch(() => {});
    setItems([]);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const Icon = category ? getIcon(category.icon) : null;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate("/categories")}
          className="inline-flex items-center gap-2 text-sm font-medium text-[#D4AF37] hover:underline mb-8"
          data-testid="back-to-categories-btn"
        >
          <ArrowLeft size={16} /> Back to categories
        </button>

        {category && (
          <div className="flex items-center gap-5 mb-10">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${category.color}18`, color: category.color }}
            >
              {Icon && <Icon size={30} strokeWidth={1.75} />}
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0F0F0F]">
                {category.name}
              </h1>
              <p className="text-[#687280] mt-1">{category.description}</p>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#687280]" data-testid="category-loading">Loading ideas...</p>
        ) : items.length === 0 ? (
          <div className="bg-[#F7F7F7] rounded-2xl border border-dashed border-gray-200 py-16 px-8 text-center" data-testid="category-empty-state">
            <p className="font-display text-xl font-semibold text-[#0F0F0F]">Ideas coming soon</p>
            <p className="text-[#687280] mt-2">We're crafting premium business ideas for this category. Check back shortly.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#687280] mb-6" data-testid="category-ideas-count">
              {total} {total === 1 ? "idea" : "ideas"}
            </p>
            <VirtualBusinessGrid
              businesses={items}
              onEndReached={() => hasMore && load(false, items.length)}
              loadingMore={loadingMore}
            />
          </>
        )}
      </section>
    </Layout>
  );
}
